import type { ReactNode } from "react";
import { T } from "../tokens";
import { RecordIcon, type RecordIconVariant } from "./RecordIcon";
import { TextBtn } from "./TextBtn";

export interface MeterButtonProps {
  /** Which tape-deck glyph sits before the label. Optional when `customIcon`
   *  is supplied (e.g. a ShareIcon for 送出, which isn't a transport key). */
  icon?: RecordIconVariant;
  /** Override the leading glyph with any node — for non-transport actions like
   *  送出 (ShareIcon). Render it in `currentColor` so it follows the ink. */
  customIcon?: ReactNode;
  /** The spaced 中文 label, e.g.「· 開 始 錄 音」. */
  children: ReactNode;
  onClick?: () => void;
  /** Ink colour for icon + label. Default ink-900. */
  color?: string;
  disabled?: boolean;
}

/**
 * Retro tape-deck "meter" button: a self-contained RecordIcon + label inside a
 * 1px hairline square frame, like a physical key on a 1970s recorder / platform
 * instrument panel. Pure grayscale.
 *
 * Wraps TextBtn so it reuses the existing press feedback (scale 0.93 +
 * ink-veil background) — the frame is just a containing border. The frame
 * border is on the outer div; TextBtn keeps its own padding/min-size and
 * carries the icon + label as children.
 */
export function MeterButton({
  icon,
  customIcon,
  children,
  onClick,
  color = T.ink900,
  disabled = false,
}: MeterButtonProps) {
  const ink = disabled ? T.ink350 : color;
  return (
    <div
      style={{
        display: "inline-block",
        border: `1px solid ${disabled ? T.ink200 : T.ink700}`,
        // --radius:0 — square corners, no box-shadow.
      }}
    >
      <TextBtn
        onClick={onClick}
        color={ink}
        disabled={disabled}
        mono={false}
        size={15}
        ls="0.16em"
        style={{ padding: "10px 18px", minWidth: 0 }}
      >
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          {customIcon ?? (icon && <RecordIcon variant={icon} size={16} color={ink} />)}
          {children}
        </span>
      </TextBtn>
    </div>
  );
}
